/**
 * This script is used by the parent page to embed the widget.
 * It creates an iframe.
 */

'use strict';

import EventEmitter from 'common/event-emitter';
import * as Constants from './lib/Constants';
import MessengerManager from './lib/MessengerManager';

function reduceToAllowedConfig(config) {
  return Constants.ALLOWED_CONFIG_PROPS.reduce((acc, k) => {
    if (config[k] !== undefined) {
      acc[k] = config[k];
    }
    return acc;
  }, {});
}

function checkValid(value, predicate) {
  if (value === undefined) {
    return true;
  }
  return predicate ? predicate(value) : value.length > 0;
}

// Public API

/**
 * The API for interacting with the Flow XO Web Messenger.
 *
 * Methods (see methods below for more details):
 * - sendMessage: send a message as the user
 * - setConfig: set the config for current and future messenger windows
 * - getState: get the state and config of the current window
 * - create: create a new messenger window
 * - destroy: shutdown the current messenger window
 * - toggle: close any open messenger window or create one if none
 * - clear: clears the chat history
 * - forget:  deletes the user data in local storage so the next time the conversation loads it will be a new one.
 *
 * The API singleton published events about the messenger lifecycle.
 *
 * Subscribe using the `.on(eventName, callback<eventData>)` method.
 *
 * You can unsubscribe using `.removeListeners(<optional eventName>)`. If no
 * event name is specified all listeners are removed.
 *
 * Note for each event a convinience methods exists too named
 * `.on<EventName>(callback)`, e.g. `.onMessageReceived(callback)`. The callback
 * is passed the event data.
 *
 * Events:
 * - created
 * - destroyed
 * - messageReceived
 * - messageSent
 * - configUpdated
 * - stateChanged
 *
 */

export default class MessengerAPI extends EventEmitter {
  constructor(id) {
    super();

    this.manager = new MessengerManager();
    this.id = id;

    // Bind to this.manager to proxy events
    this.EVENT_NAMES.forEach((name) => {
      this.manager.on(name, (data) => {
        this.emit(name, data);
      });
    });

    const self = this;
    this.on('created', function () {
      self.setConfig({
        metadata: {
          hostUrl: window.location.href,
          parentUrl:
            window.location != window.parent.location
              ? document.referrer
              : document.location.href,
        },
      });
    });
  }

  /**
   * Send a message from the current messenger window. Some metadata about the
   * user's client is also sent with every message. Any custom metadata specifed
   * in the `metadata` parameter will be added to the default set, and override
   * any of the default metadata options if they have the same property name.
   *
   * @param text The message text body
   * @param metadata Any optional metadata parameters.
   * @returns Boolean Returns false if there was no messenger window yet created
   */
  sendMessage(text, metadata) {
    if (!text) {
      throw new Error('The message cannot be blank.');
    }

    return this.manager.sendMessage(text, metadata || {});
  }

  /**
   * Set the messenger configuration for the current or any future messenger
   * windows. You can specify one or more property
   *
   * Valid config values are:
   * - fullscreen: Boolean determines whether messenger is shown as a sidebar or fullscreen
   * - headerText: String the message to display in the window top bar
   * - customPromptMessage: String the prompt placeholder text shown in the message box.
   * - color: String a valid CSS color value
   * - botIcon: String a URL to the icon to display for the bot
   *
   * @param config Object the messenger configuration options
   * @returns Boolean Returns false if any of the configuration options are invalid
   */
  setConfig(config) {
    config = reduceToAllowedConfig(config);

    const validMode = checkValid(
      config.mode,
      (x) => this.MODES.indexOf(x) > -1
    );
    const validHeaderText = checkValid(config.headerText);
    const validCustomPromptMessage = checkValid(config.customPromptMessage);
    const validColor = checkValid(config.color);
    const validIcon = checkValid(config.botIcon);
    const validStandalone = checkValid(config.standalone, (s) => s === true);
    const validAllowBackgroundScroll = checkValid(
      config.allowBackgroundScroll,
      (s) => s === true
    );
    const validBotId = checkValid(config.botId);
    const validMetadata = checkValid(config.metadata, (v) => v);

    if (
      !(
        validMode &&
        validHeaderText &&
        validColor &&
        validIcon &&
        validStandalone &&
        validBotId &&
        validAllowBackgroundScroll &&
        validMetadata &&
        validCustomPromptMessage
      )
    ) {
      return false;
    }
    this.manager.setConfig(config);
    return true;
  }

  /**
   * Return the current messenger configuration and the state of the messenger
   * window if one is open
   * @returns Object the current configuration properties and state of the messenger window or undefined if no window is open
   */
  getState() {
    return this.manager.getInstanceState();
  }

  /**
   * Create a new messenger window. If one is already open it will destroyed
   * and a new window created.
   */
  create(containerElement) {
    this.manager.createInstance(containerElement, this.id);
  }

  /**
   * Destroy the current open messenger window.
   * @returns Boolean true if a window was open and destroyed
   */
  destroy() {
    return this.manager.destroyCurrent();
  }

  /**
   *  Clears the history of the current open messenger window.
   */
  clear() {
    return this.manager.clearCurrent();
  }

  forget() {
    return this.manager.forget();
  }

  /**
   * Toggle the state of the messenger window.
   * If the window is open it will be destroyed, and vice versa.
   * @returns Boolean true if a window is opened and false if closed.
   */
  toggle() {
    return this.manager.toggleCurrentState();
  }
}

const EVENT_NAMES = [...Constants.LIFECYCLE_EVENTS];

MessengerAPI.prototype.MODES = [...Constants.MESSENGER_MODES];
MessengerAPI.prototype.EVENT_NAMES = EVENT_NAMES;

// Setup convinience method for adding event listeners
EVENT_NAMES.forEach((name) => {
  MessengerAPI.prototype[`on${name[0].toUpperCase() + name.slice(1)}`] =
    function (callback) {
      if (callback) {
        this.on(name, callback);
      }
    };
  MessengerAPI.prototype[`off${name[0].toUpperCase() + name.slice(1)}`] =
    function (callback) {
      if (callback) {
        this.off(name, callback);
      }
    };
});



// WEBPACK FOOTER //
// ./src/messenger/embed.js